import React, { useState, useEffect } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import JobReferralsApi from '../../../../api/jobReferrals';

const STATUS_OPTIONS = [
  { value: '', label: 'כל הסטטוסים' },
  { value: 'pending', label: 'ממתין' },
  { value: 'eligible', label: 'זכאי' },
  { value: 'needs_review', label: 'דורש בדיקה ידנית' },
  { value: 'verification_failed', label: 'אימות נכשל' },
  { value: 'approved', label: 'אושר' },
  { value: 'denied', label: 'נדחה' },
];

const STATUS_LABELS = {
  pending: 'ממתין',
  eligible: 'זכאי',
  needs_review: 'דורש בדיקה ידנית',
  verification_failed: 'אימות נכשל',
  approved: 'אושר',
  denied: 'נדחה',
};
const STATUS_COLORS = {
  pending: 'yellow',
  eligible: 'blue',
  needs_review: 'orange',
  verification_failed: 'red',
  approved: 'green',
  denied: 'red',
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const PAGE_SIZE = 50;

export default function RewardsTab({ isSuperAdmin }) {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [govIdDrafts, setGovIdDrafts] = useState({});

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const fetchRewards = async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await JobReferralsApi.getRewards(
        statusFilter || undefined,
        undefined,
        currentPage,
        PAGE_SIZE
      );
      setRewards(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast({ title: 'שגיאה בטעינת תגמולים', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchRewards(1);
  }, [statusFilter]);

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      await JobReferralsApi.updateReward(id, action);
      toast({
        title: action === 'approve' ? 'תגמול אושר' : 'תגמול נדחה',
        status: 'success',
        duration: 2000,
      });
      fetchRewards(page);
    } catch {
      toast({ title: 'שגיאה בעדכון תגמול', status: 'error', duration: 3000 });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReverify = async (id) => {
    const draft = (govIdDrafts[id] || '').trim();
    setActionLoading(id);
    try {
      await JobReferralsApi.reverifyReward(id, draft || undefined);
      toast({ title: 'אומת מחדש', status: 'success', duration: 2000 });
      setGovIdDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fetchRewards(page);
    } catch (err) {
      toast({
        title: err?.response?.data?.message || 'שגיאה באימות',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Card mt={4}>
      <Flex mb={4} gap={3} align="center">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          w="200px"
          dir="rtl"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" dir="rtl">
            <Thead>
              <Tr>
                <Th color={textColor} borderColor={borderColor}>ממליץ</Th>
                <Th color={textColor} borderColor={borderColor}>מועמד</Th>
                <Th color={textColor} borderColor={borderColor}>סוג / משרה</Th>
                <Th color={textColor} borderColor={borderColor}>תאריך קבלה</Th>
                <Th color={textColor} borderColor={borderColor}>סכום</Th>
                <Th color={textColor} borderColor={borderColor}>תאריך זכאות</Th>
                <Th color={textColor} borderColor={borderColor}>סטטוס</Th>
                <Th color={textColor} borderColor={borderColor}>פעולות</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rewards.length === 0 && (
                <Tr>
                  <Td colSpan={8} textAlign="center" color="gray.400">
                    אין תגמולים
                  </Td>
                </Tr>
              )}
              {rewards.map((reward) => {
                const daysLeft = daysUntil(reward.eligibleAt);
                return (
                  <Tr key={reward.id}>
                    <Td color={textColor}>
                      {reward.referrer
                        ? `${reward.referrer.firstName} ${reward.referrer.lastName}`
                        : '—'}
                    </Td>
                    <Td color={textColor}>{reward.candidate?.fullName || '—'}</Td>
                    <Td color={textColor}>
                      {reward.referralType === 'general' ? (
                        <Badge colorScheme="purple" fontSize="11px">כללי</Badge>
                      ) : (
                        reward.job?.title || '—'
                      )}
                    </Td>
                    <Td color={textColor} fontSize="sm">
                      {reward.hireDate
                        ? new Date(reward.hireDate).toLocaleDateString('he-IL')
                        : '—'}
                    </Td>
                    <Td color={textColor} fontWeight="600">
                      ₪{Number(reward.amount).toLocaleString()}
                    </Td>
                    <Td fontSize="sm">
                      <Text color={textColor}>
                        {reward.eligibleAt
                          ? new Date(reward.eligibleAt).toLocaleDateString('he-IL')
                          : '—'}
                      </Text>
                      {reward.status === 'pending' && daysLeft > 0 && (
                        <Text fontSize="xs" color="orange.400">
                          עוד {daysLeft} ימים
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[reward.status] || 'gray'}>
                        {STATUS_LABELS[reward.status] || reward.status}
                      </Badge>
                      {reward.status === 'approved' && reward.approvedAt && (
                        <Text fontSize="xs" color="gray.400" mt={1}>
                          {new Date(reward.approvedAt).toLocaleDateString('he-IL')}
                        </Text>
                      )}
                      {(reward.status === 'needs_review' || reward.status === 'verification_failed') &&
                        reward.verificationNotes && (
                          <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">
                            {reward.verificationNotes}
                          </Text>
                        )}
                    </Td>
                    <Td>
                      {isSuperAdmin && (
                        <Flex direction="column" gap={2} align="stretch">
                          {(reward.status === 'eligible' || reward.status === 'needs_review') && (
                            <HStack spacing={2}>
                              <Button
                                size="xs"
                                colorScheme="green"
                                isLoading={actionLoading === reward.id}
                                onClick={() => handleAction(reward.id, 'approve')}
                              >
                                אשר
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                isLoading={actionLoading === reward.id}
                                onClick={() => handleAction(reward.id, 'deny')}
                              >
                                דחה
                              </Button>
                            </HStack>
                          )}
                          {reward.status === 'needs_review' && (
                            <HStack spacing={2}>
                              <Input
                                size="xs"
                                placeholder="ת.ז. (9 ספרות)"
                                inputMode="numeric"
                                maxW="120px"
                                value={govIdDrafts[reward.id] || ''}
                                onChange={(e) =>
                                  setGovIdDrafts((prev) => ({
                                    ...prev,
                                    [reward.id]: e.target.value,
                                  }))
                                }
                              />
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="outline"
                                isLoading={actionLoading === reward.id}
                                onClick={() => handleReverify(reward.id)}
                              >
                                אמת מחדש
                              </Button>
                            </HStack>
                          )}
                        </Flex>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      {total > 0 && (
        <Flex justify="center" align="center" gap={4} mt={4}>
          <Button size="sm" onClick={() => { const p = page - 1; setPage(p); fetchRewards(p); }} isDisabled={page === 1}>
            הקודם
          </Button>
          <Flex direction="column" align="center">
            <Text fontSize="sm">{page} / {Math.ceil(total / PAGE_SIZE)}</Text>
            <Text fontSize="xs" color="gray.400">{total} סה"כ</Text>
          </Flex>
          <Button size="sm" onClick={() => { const p = page + 1; setPage(p); fetchRewards(p); }} isDisabled={page >= Math.ceil(total / PAGE_SIZE)}>
            הבא
          </Button>
        </Flex>
      )}
    </Card>
  );
}
